import { useCallback, useEffect, useState } from "react";
import Layout from "layout/Layout";
import DateRangeSelector from "pages/dashboard-overview/components/DateRangeSelector";
import useHttp from "hooks/useHttp";
import useAnalyticsFilters from "hooks/useAnalyticsFilters";
import { getGhlPipelineMetrics } from "services/ghl-integration.service";
import { formatDate } from "pages/analytics/components/analyticsFormatters";
import PipelineStageDistribution from "pages/analytics/sales-funnel/components/PipelineStageDistribution";
import RevenueByStageTable from "pages/analytics/sales-funnel/components/RevenueByStageTable";
import GhlPipelineKpiCards from "pages/analytics/sales-funnel/components/GhlPipelineKpiCards";
import GhlSyncDropdown from "pages/analytics/sales-funnel/components/GhlSyncDropdown";
import GhlConversionRates from "pages/ghl-pipeline/components/GhlConversionRates";

export default function GhlPipeline() {
  const api = useHttp();
  const { filters, dateRange, setDateRange, customRange, setCustomRange } =
    useAnalyticsFilters("thisMonth");
  const [ghlData, setGhlData] = useState(null);
  const [ghlLoading, setGhlLoading] = useState(true);
  const [ghlError, setGhlError] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadGhlMetrics = useCallback(async () => {
    setGhlLoading(true);
    setGhlError(null);
    try {
      const result = await getGhlPipelineMetrics(api, { filters });
      setGhlData(result);
    } catch {
      setGhlData(null);
      setGhlError("Failed to load pipeline KPIs.");
    } finally {
      setGhlLoading(false);
    }
  }, [api, filters]);

  useEffect(() => {
    loadGhlMetrics();
  }, [loadGhlMetrics, refreshKey]);

  const handleSyncComplete = useCallback(async () => {
    setRefreshKey((k) => k + 1);
    await loadGhlMetrics();
  }, [loadGhlMetrics]);

  return (
    <Layout>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">GHL Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1">
            CRM pipeline stages, revenue by stage, and sync controls
          </p>
          {ghlData?.lastSyncedAt && !ghlLoading && (
            <p className="text-xs text-gray-500 mt-1">
              Last synced {formatDate(ghlData.lastSyncedAt)}
            </p>
          )}
        </div>

        <GhlSyncDropdown
          api={api}
          isSyncing={isSyncing}
          setIsSyncing={setIsSyncing}
          setSyncError={setSyncError}
          onComplete={handleSyncComplete}
        />
      </div>

      {syncError && (
        <p className="text-sm text-red-600 mb-4">{syncError}</p>
      )}

      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:justify-between">
        <DateRangeSelector
          selected={dateRange}
          onChange={setDateRange}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
        />
      </div>

      <GhlPipelineKpiCards
        data={ghlData}
        isLoading={ghlLoading}
        error={ghlError}
      />
      <div className="mb-6">
        <GhlConversionRates data={ghlData} isLoading={ghlLoading} />
      </div>
      <PipelineStageDistribution api={api} filters={filters} />
      <RevenueByStageTable api={api} filters={filters} />
    </Layout>
  );
}
